/**
 * Country flags from the shared SVG sprite.
 *
 * The imports register one `<symbol>` per file into the sprite that
 * `svg-sprite-loader` assembles at build time. The symbol ids carry no `leap-`
 * prefix — `webpack.config.js` derives the prefix from the SVG's own path, and
 * these files live outside `app/leap/` — so the id is the bare file name (`DE`,
 * `GB`, …), which is what the map below is keyed by.
 *
 * The map holds each flag's `viewBox` because the imported module does not: the
 * loader leaves it undefined here, which is why `./index.jsx` carries `||`
 * fallbacks for its own glyphs. Without one the browser falls back to the
 * default 300x150 SVG viewport, so a caller sizing one axis gets a 150px-tall
 * flag. Holding the real value lets each artwork keep its own proportions —
 * they differ, most flags being 3:2 and Ukraine 4:3, so a single shared ratio
 * would letterbox the rest. `flags.test.js` reads the SVGs off disk and holds
 * these values against them, so a replaced artwork cannot drift.
 *
 * Deliberately NOT re-exported from `./index.jsx`, so reaching for one ordinary
 * glyph does not drag all 76 flags in. That only spares *other* icon consumers
 * though — this module imports the whole set unconditionally, and the header
 * pulls it in on every page. Trimming what ships (a per-locale subset, a
 * dynamic import) is a change to these imports, not to where the barrel sits.
 */
import '../../../static/flags/AE.svg'
import '../../../static/flags/AL.svg'
import '../../../static/flags/AM.svg'
import '../../../static/flags/AR.svg'
import '../../../static/flags/AT.svg'
import '../../../static/flags/AU.svg'
import '../../../static/flags/AZ.svg'
import '../../../static/flags/BA.svg'
import '../../../static/flags/BE.svg'
import '../../../static/flags/BG.svg'
import '../../../static/flags/BN.svg'
import '../../../static/flags/BR.svg'
import '../../../static/flags/BY.svg'
import '../../../static/flags/CA.svg'
import '../../../static/flags/CH.svg'
import '../../../static/flags/CL.svg'
import '../../../static/flags/CN.svg'
import '../../../static/flags/CO.svg'
import '../../../static/flags/CR.svg'
import '../../../static/flags/CY.svg'
import '../../../static/flags/CZ.svg'
import '../../../static/flags/DE.svg'
import '../../../static/flags/DK.svg'
import '../../../static/flags/EE.svg'
import '../../../static/flags/EG.svg'
import '../../../static/flags/ES.svg'
import '../../../static/flags/EU.svg'
import '../../../static/flags/FI.svg'
import '../../../static/flags/FR.svg'
import '../../../static/flags/GB.svg'
import '../../../static/flags/GE.svg'
import '../../../static/flags/GR.svg'
import '../../../static/flags/HK.svg'
import '../../../static/flags/HR.svg'
import '../../../static/flags/HU.svg'
import '../../../static/flags/ID.svg'
import '../../../static/flags/IE.svg'
import '../../../static/flags/IL.svg'
import '../../../static/flags/IN.svg'
import '../../../static/flags/IR.svg'
import '../../../static/flags/IS.svg'
import '../../../static/flags/IT.svg'
import '../../../static/flags/JP.svg'
import '../../../static/flags/KG.svg'
import '../../../static/flags/KR.svg'
import '../../../static/flags/KZ.svg'
import '../../../static/flags/LT.svg'
import '../../../static/flags/LU.svg'
import '../../../static/flags/LV.svg'
import '../../../static/flags/MX.svg'
import '../../../static/flags/MY.svg'
import '../../../static/flags/NL.svg'
import '../../../static/flags/NO.svg'
import '../../../static/flags/NZ.svg'
import '../../../static/flags/PA.svg'
import '../../../static/flags/PE.svg'
import '../../../static/flags/PH.svg'
import '../../../static/flags/PL.svg'
import '../../../static/flags/PR.svg'
import '../../../static/flags/PT.svg'
import '../../../static/flags/QA.svg'
import '../../../static/flags/RO.svg'
import '../../../static/flags/RS.svg'
import '../../../static/flags/RU.svg'
import '../../../static/flags/SA.svg'
import '../../../static/flags/SE.svg'
import '../../../static/flags/SG.svg'
import '../../../static/flags/SI.svg'
import '../../../static/flags/SK.svg'
import '../../../static/flags/TH.svg'
import '../../../static/flags/TR.svg'
import '../../../static/flags/UA.svg'
import '../../../static/flags/UK.svg'
import '../../../static/flags/US.svg'
import '../../../static/flags/XB.svg'
import '../../../static/flags/ZA.svg'

const FLAG_VIEW_BOXES = {
    AE: '0 0 513 343',
    AL: '0 0 513 343',
    AM: '0 0 513 343',
    AR: '0 0 513 343',
    AT: '0 0 513 343',
    AU: '0 0 513 343',
    AZ: '0 0 513 343',
    BA: '0 0 513 343',
    BE: '0 0 513 343',
    BG: '0 0 513 343',
    BN: '0 0 513 343',
    BR: '0 0 513 343',
    BY: '0 0 513 343',
    CA: '0 0 513 343',
    CH: '0 0 513 343',
    CL: '0 0 513 343',
    CN: '0 0 513 343',
    CO: '0 0 513 343',
    CR: '0 0 513 343',
    CY: '0 0 513 343',
    CZ: '0 0 513 343',
    DE: '0 0 513 343',
    DK: '0 0 513 343',
    EE: '0 0 513 343',
    EG: '0 0 513 343',
    ES: '0 0 513 343',
    EU: '0 0 513 343',
    FI: '0 0 513 343',
    FR: '0 0 513 343',
    GB: '0 0 513 343',
    GE: '0 0 513 343',
    GR: '0 0 513 343',
    HK: '0 0 513 343',
    HR: '0 0 513 343',
    HU: '0 0 513 343',
    ID: '0 0 513 343',
    IE: '0 0 513 343',
    IL: '0 0 513 343',
    IN: '0 0 513 343',
    IR: '0 0 513 343',
    IS: '0 0 513 343',
    IT: '0 0 513 343',
    JP: '0 0 513 343',
    KG: '0 0 513 344',
    KR: '0 0 513 343',
    KZ: '0 0 513 343',
    LT: '0 0 513 343',
    LU: '0 0 513 343',
    LV: '0 0 513 343',
    MX: '0 0 513 343',
    MY: '0 0 513 343',
    NL: '0 0 513 343',
    NO: '0 0 513 343',
    NZ: '0 0 513 343',
    PA: '0 0 513 343',
    PE: '0 0 513 343',
    PH: '0 0 513 343',
    PL: '0 0 513 343',
    PR: '0 0 513 343',
    PT: '0 0 513 343',
    QA: '0 0 513 343',
    RO: '0 0 513 343',
    RS: '0 0 513 343',
    RU: '0 0 513 343',
    SA: '0 26.67 640 426.67',
    SE: '0 0 513 343',
    SG: '0 0 513 343',
    SI: '0 0 513 343',
    SK: '0 0 513 343',
    TH: '0 0 513 343',
    TR: '0 0 513 343',
    UA: '0 0 640 480',
    UK: '0 0 513 343',
    US: '0 0 513 343',
    XB: '0 0 513 342',
    ZA: '0 0 513 343'
}

/**
 * Country codes the sprite can render, derived from the map so the two cannot
 * disagree. A caller uses this to tell "no flag for this country" apart from
 * "flag failed to render" — a `<use>` pointing at a missing symbol paints
 * nothing and leaves a hole in the layout.
 */
export const FLAG_COUNTRY_CODES = Object.keys(FLAG_VIEW_BOXES)

/**
 * The `viewBox` for a country's flag, or undefined when there is no flag.
 *
 * @param {string} code - Uppercase ISO 3166-1 alpha-2 code.
 * @returns {string|undefined}
 */
export const flagViewBox = (code) => FLAG_VIEW_BOXES[code]
